import { ChangeEvent, ReactNode, useEffect, useRef, useState } from 'react'
import { useController, useFormContext } from 'react-hook-form'
import toast from 'react-hot-toast'
import { Box, FormControl, FormHelperText, FormLabel, styled, SxProps } from '@mui/material'
import accept from 'attr-accept'
import heic2any from 'heic2any'

import { DefaultElementProps, File, Validations } from 'common/interfaces'
import fileValidation from 'elements/FileUpload/common/fileValidations'
import ButtonUploadMob from 'elements/FileUpload/components/ButtonUploadMob'
import FilePreview from 'elements/FileUpload/components/FilePreview'
import FilePreviewLoading from 'elements/FileUpload/components/FilePreviewLoading'
import ImagePreview from 'elements/FileUpload/components/ImagePreview'
import ImagePreviewLoading from 'elements/FileUpload/components/ImagePreviewLoading'
import UploadWrapper from 'elements/FileUpload/components/UploadWrapper'
import { useIsOnline, useIsPhone } from 'utils/hook'
import { getFileExtensionAndUniqName, S3Bucket, useUploadFileToAWS } from 'utils/uploadFileToS3'

import { FileUpload as FileUploadProps } from './interface'

const imageFilesWrap: SxProps = {
  display: 'grid',
  gridTemplateColumns: {
    xs: 'repeat(4, 80px)',
    sm: 'repeat(5, 80px)',
  },
  columnGap: '20px',
  rowGap: '20px',
  '@media (min-width: 320px) and (max-width: 500px)': {
    gridTemplateColumns: 'repeat(3, 80px)',
  },
  '@media (max-width: 359px)': {
    columnGap: '6vw',
  },
  '@media (min-width: 360px) and (max-width: 500px)': {
    columnGap: '11vw',
  },
}

const Wrapper = styled(Box)(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    justifyContent: 'center',
    flexDirection: 'column',
  },
}))

type FileType = Exclude<FileUploadProps['type'], undefined>

const megabyte = 1048576

const acceptedTypes: Record<FileType, string> = {
  document:
    'application/msword, application/vnd.ms-excel, application/vnd.ms-powerpoint, text/plain, application/pdf, application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  image: 'image/*, .heic, .heif',
}

export default function FileUpload({
  name,
  type = 'image',
  label,
  onFocus,
  onChange,
  validations,
  maxFileSize = 20, // value in MB
  maxTotalSize = 1024, // value in MB
}: FileUploadProps & DefaultElementProps): JSX.Element {
  const isOnline = useIsOnline()
  const { mutateAsync: sendFileToAWS } = useUploadFileToAWS()

  const { control, setValue, setError: setFieldError } = useFormContext()
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
    defaultValue: [],
    rules: fileValidation(validations),
  })
  const validationsMaxRef = useRef<Validations['max']>(validations.max)

  const isMobile = useIsPhone()
  const [files, setFiles] = useState<File[]>(field.value)

  const [imageElements, setImageElements] = useState<File[]>(field.value)

  const [loadingFilesLength, setLoadingFilesLength] = useState(0)

  const updateField = (updatedFiles: File[]): void => {
    field.onChange({ target: { value: updatedFiles } })
  }

  async function send(fileList: FileList): Promise<void> {
    let sendingFilesLength = fileList.length

    if (validationsMaxRef.current?.value != null) {
      const totalFileLength = validationsMaxRef.current?.value - files.length - sendingFilesLength
      if (totalFileLength < 0) {
        sendingFilesLength = validationsMaxRef.current?.value - files.length
      }
    }

    let totalSize = 0

    files.forEach((file) => {
      totalSize += file.size
    })

    for (let i = 0; i < sendingFilesLength; i++) {
      const oneFile = fileList[i]
      const oneFileName = oneFile.name

      totalSize += oneFile.size
      if (oneFile.size >= maxFileSize * megabyte) {
        setLoadingFilesLength((prevState) => prevState - 1)
        toast.error(
          `The file ${oneFileName} is too big. The maximum file size is ${maxFileSize}Mb`,
          {
            duration: 10000,
          }
        )
        continue
      }

      if (maxTotalSize && totalSize >= maxTotalSize * megabyte) {
        setLoadingFilesLength((prevState) => prevState - 1)
        toast.error(
          `The file ${oneFileName} could not be processed. The total size of all files must not exceed ${maxTotalSize}Mb`,
          {
            duration: 10000,
          }
        )
        continue
      }

      try {
        let blobToUpload: Blob = oneFile
        const isHeic = isFileHeic(oneFile)
        if (isHeic) {
          blobToUpload = await convertHeicToJpeg(oneFile)
        }

        const { fileUniqName, fileExtension } = getFileExtensionAndUniqName(
          oneFileName,
          isHeic ? 'jpeg' : undefined
        )
        const newFile = {
          extension: fileExtension,
          file: process.env.AWS_PATH! + process.env.AWS_FILE_PATH! + fileUniqName,
          name: oneFileName,
          size: oneFile.size,
          ...(type === 'image' && {
            fileKeyUrl: process.env.AWS_FILE_PATH! + fileUniqName,
          }),
        }

        const updateFilesAndField = (): void => {
          setFiles((prev) => {
            const updatedFiles = [...prev, newFile]
            updateField(updatedFiles)

            return updatedFiles
          })
        }

        const awsParams = {
          Body: blobToUpload,
          Bucket: process.env.AWS_BUCKET_NAME!,
          Key: process.env.AWS_FILE_PATH! + fileUniqName,
        }
        await sendFileToAWS(awsParams, {
          onSuccess: () => {
            updateFilesAndField()
            setLoadingFilesLength((prevState) => prevState - 1)
          },
          onError: () => {
            setLoadingFilesLength((prevState) => prevState - 1)

            if (!isOnline) {
              updateFilesAndField()

              return
            }

            setError("Sorry, your file didn't load")
          },
        }).finally(() => {
          console.log('FILES', files)
        })
      } catch (error) {
        console.log('Error with:', error)
      }
    }
  }

  async function convertHeicToJpeg(blob: Blob): Promise<Blob> {
    const jpegBlob = await heic2any({
      blob,
      toType: 'image/jpeg',
      quality: 1.0,
    })
    // Blob is possibly an array
    return Array.isArray(jpegBlob) ? jpegBlob[0] : jpegBlob
  }

  const isFileHeic = (file: globalThis.File): boolean => {
    if (file.type) {
      return file.type.toLowerCase() === 'image/heic' || file.type.toLowerCase() === 'image/heif'
    }

    const fileName = file.name.toLowerCase() // Convert to lowercase
    return fileName.endsWith('.heic') || fileName.endsWith('.heif')
  }

  function remove(index: number): void {
    setFiles((prev) => {
      const updatedFiles = [...prev.slice(0, index), ...prev.slice(index + 1)]
      updateField(updatedFiles)

      return updatedFiles
    })
  }

  function setError(message: string): void {
    setFieldError(name, { message })
  }

  function handleChange(files: FileList): void {
    // validate using attr-accept
    const areValid = Array.from(files).every((file) => accept(file, acceptedTypes[type]))

    if (areValid) {
      void send(files)
    } else {
      setError('Files with this type are not accepted')
    }
  }

  function onFilesChange(event: ChangeEvent<HTMLInputElement>): void {
    const currentTargetFiles = event.currentTarget.files
    const currentTargetFilesLength = currentTargetFiles?.length

    if (currentTargetFiles === null) {
      return
    }

    if (validationsMaxRef.current !== undefined) {
      const validationsMaxRefValue = validationsMaxRef.current?.value
      const totalFileLength = validationsMaxRefValue - files.length - currentTargetFilesLength!

      if (files.length < validationsMaxRefValue) {
        if (totalFileLength >= 0) {
          setLoadingFilesLength(currentTargetFilesLength!)
        } else {
          setLoadingFilesLength(validationsMaxRefValue - files.length)
        }

        handleChange(currentTargetFiles)
      }

      if (files.length + currentTargetFilesLength! > validationsMaxRefValue) {
        toast.error(`The maximum count of files is ${validationsMaxRefValue}`, {
          duration: 10000,
        })
      }
    } else {
      setLoadingFilesLength(currentTargetFilesLength!)
      handleChange(currentTargetFiles)
    }
  }

  function onRemove(index: number) {
    return () => remove(index)
  }

  let content: ReactNode = null

  if (type === 'document') {
    content = (
      <Wrapper className="file-upload-wrap file-upload-wrap__doc">
        {!isMobile && <UploadWrapper name={name} onChange={handleChange} />}
        {files?.map((file, i) => (
          <FilePreview key={file.file} file={file} onRemove={onRemove(i)} />
        ))}
        {Array.from({ length: loadingFilesLength }, (_, index) => (
          <FilePreviewLoading key={index} />
        ))}
        {isMobile && <ButtonUploadMob name={name} />}
      </Wrapper>
    )
  }

  if (type === 'image') {
    content = (
      <Wrapper className="file-upload-wrap file-upload-wrap__image">
        {!isMobile && <UploadWrapper name={name} onChange={handleChange} withImage />}
        <Box className="file-upload-files-wrap" sx={imageFilesWrap}>
          {imageElements?.map(({ url, file, name }, i) => (
            <ImagePreview
              src={url!}
              onRemove={onRemove(i)}
              name={name}
              isOnline={isOnline}
              key={file}
            />
          ))}
          {Array.from({ length: loadingFilesLength }, (_, index) => (
            <ImagePreviewLoading key={index} />
          ))}
        </Box>
        {isMobile && <ButtonUploadMob name={name} withImage />}
      </Wrapper>
    )
  }

  const createArrayOfImages = async (): Promise<void> => {
    const elements = await Promise.all(
      files.map(async (file) => {
        const url = await S3Bucket.getSignedUrlPromise('getObject', {
          Bucket: process.env.AWS_BUCKET_NAME!,
          Key: file.fileKeyUrl,
          Expires: 86400,
        })
        return { ...file, url }
      })
    )

    setImageElements(elements)
  }

  useEffect(() => {
    setValue(name, files)
    onChange?.({ value: files })

    if (type === 'image' && isOnline) {
      void createArrayOfImages()
    }

    if (type === 'image' && !isOnline) {
      setImageElements(files)
    }
  }, [files, isOnline])

  return (
    <FormControl error={error !== undefined}>
      <input
        hidden
        type="file"
        id={name}
        multiple
        accept={acceptedTypes[type]}
        onChange={onFilesChange}
        onFocus={onFocus}
      />
      {label !== undefined && <FormLabel>{label}</FormLabel>}
      {content}
      {error !== undefined && <FormHelperText>{error.message}</FormHelperText>}
    </FormControl>
  )
}
